import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, ArrowDown, ArrowUp, Camera, Layers, MapPin, Pencil, Plus, Save, Trash2, X } from "lucide-react";
import AppShell from "../components/AppShell";
import { api, API_URL } from "../lib/api";

const money = (v) => `$${Number(v || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MOVEMENT_LABEL = { entry: "Entrada", exit: "Salida", adjustment: "Ajuste" };

function ProductHero({ product, onPhotoUploaded }) {
  const [url, setUrl] = useState("");
  const fileInput = useRef(null);
  const load = useCallback(() => {
    if (!product?.has_photo) { setUrl(""); return; }
    const token = window.localStorage.getItem("session_token");
    fetch(`${API_URL}/inventory/products/${product.id}/photo`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => (r.ok ? r.blob() : null))
      .then((b) => b && setUrl(URL.createObjectURL(b)))
      .catch(() => setUrl(""));
  }, [product?.has_photo, product?.id]);
  useEffect(() => { load(); }, [load]);
  const upload = async (file) => {
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    await api.post(`/inventory/products/${product.id}/photo`, body, { withCredentials: true });
    onPhotoUploaded?.();
  };
  return (
    <div className="inv-hero-photo" data-testid="inventory-detail-photo">
      {url ? <img src={url} alt={product.name} /> : <Camera size={48} strokeWidth={1.3} />}
      <button type="button" onClick={() => fileInput.current?.click()} data-testid="inventory-detail-photo-button"><Camera size={14} /> {product.has_photo ? "Cambiar foto" : "Subir foto"}</button>
      <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: "none" }} onChange={(e) => upload(e.target.files?.[0])} data-testid="inventory-detail-photo-input" />
    </div>
  );
}

export default function InventoryDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [movements, setMovements] = useState([]);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [productForm, setProductForm] = useState({});
  const [movementForm, setMovementForm] = useState({ movement_type: "entry", quantity: 1, variation_sku: "", location: "", note: "", cost: "" });
  const [movementSaving, setMovementSaving] = useState(false);
  const [showVariation, setShowVariation] = useState(false);
  const [variationForm, setVariationForm] = useState({ name: "", size: "", color: "", model: "", quantity: 0, location: "", cost_override: "", price_override: "" });

  const load = useCallback(async () => {
    try {
      const [{ data: p }, { data: m }] = await Promise.all([
        api.get(`/inventory/products/${id}`, { withCredentials: true }),
        api.get(`/inventory/products/${id}/movements`, { withCredentials: true }),
      ]);
      setProduct(p);
      setProductForm(p);
      setMovements(m || []);
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos cargar el producto.");
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const saveProduct = async () => {
    try {
      await api.put(`/inventory/products/${id}`, productForm, { withCredentials: true });
      setEditing(false);
      load();
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos guardar los cambios.");
    }
  };

  const remove = async () => {
    if (!window.confirm("¿Eliminar producto y todo su historial?")) return;
    await api.delete(`/inventory/products/${id}`, { withCredentials: true });
    navigate("/inventario");
  };

  const recordMovement = async (event) => {
    event.preventDefault();
    setMovementSaving(true);
    setError("");
    try {
      await api.post(`/inventory/products/${id}/movements`, movementForm, { withCredentials: true });
      setMovementForm({ movement_type: "entry", quantity: 1, variation_sku: "", location: "", note: "", cost: "" });
      load();
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos registrar el movimiento.");
    } finally {
      setMovementSaving(false);
    }
  };

  const addVariation = async (event) => {
    event.preventDefault();
    try {
      await api.post(`/inventory/products/${id}/variations`, variationForm, { withCredentials: true });
      setShowVariation(false);
      setVariationForm({ name: "", size: "", color: "", model: "", quantity: 0, location: "", cost_override: "", price_override: "" });
      load();
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos añadir la variación.");
    }
  };

  const deleteVariation = async (sku) => {
    if (!window.confirm("¿Eliminar esta variación?")) return;
    await api.delete(`/inventory/products/${id}/variations/${sku}`, { withCredentials: true });
    load();
  };

  const updateVariationQuantity = async (sku, delta) => {
    const target = (product.variations || []).find((v) => v.sku === sku);
    const newQty = Math.max(0, Number(target?.quantity || 0) + delta);
    await api.put(`/inventory/products/${id}/variations/${sku}`, { quantity: newQty }, { withCredentials: true });
    load();
  };

  const locations = useMemo(() => {
    if (!product) return [];
    const list = new Map();
    (product.variations || []).forEach((v) => {
      const loc = (v.location || "Sin ubicación").trim() || "Sin ubicación";
      list.set(loc, (list.get(loc) || 0) + Number(v.quantity || 0));
    });
    if ((product.variations || []).length === 0 && product.location) {
      list.set(product.location, product.total_quantity);
    }
    return Array.from(list.entries()).map(([location, quantity]) => ({ location, quantity }));
  }, [product]);

  if (!product) {
    return <AppShell><div className="page">{error ? <p className="profile-error">{error}</p> : "Cargando..."}</div></AppShell>;
  }

  return (
    <AppShell>
      <div className="page inventory-detail">
        <Link to="/inventario" className="inv-back"><ArrowLeft size={14} /> Volver al inventario</Link>

        <section className="inv-detail-hero">
          <ProductHero product={product} onPhotoUploaded={load} />
          <div className="inv-detail-info">
            {editing ? (
              <div className="profile-grid">
                <label className="full">Nombre<input value={productForm.name || ""} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} data-testid="inventory-detail-edit-name" /></label>
                <label>Categoría<input value={productForm.category || ""} onChange={(e) => setProductForm({ ...productForm, category: e.target.value })} /></label>
                <label>Unidad<input value={productForm.unit || ""} onChange={(e) => setProductForm({ ...productForm, unit: e.target.value })} /></label>
                <label>Costo<input type="number" step="0.01" value={productForm.cost || ""} onChange={(e) => setProductForm({ ...productForm, cost: e.target.value })} data-testid="inventory-detail-edit-cost" /></label>
                <label>Precio<input type="number" step="0.01" value={productForm.selling_price || ""} onChange={(e) => setProductForm({ ...productForm, selling_price: e.target.value })} data-testid="inventory-detail-edit-price" /></label>
                <label>Alerta stock bajo<input type="number" step="1" value={productForm.low_stock_threshold ?? ""} onChange={(e) => setProductForm({ ...productForm, low_stock_threshold: e.target.value })} /></label>
                <label className="full">Descripción<textarea value={productForm.description || ""} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} /></label>
                <div className="full" style={{ display: "flex", gap: 10 }}>
                  <button type="button" className="cash-btn primary" onClick={saveProduct} data-testid="inventory-detail-save"><Save size={14} /> Guardar</button>
                  <button type="button" className="cash-btn ghost" onClick={() => { setEditing(false); setProductForm(product); }}>Cancelar</button>
                </div>
              </div>
            ) : (
              <>
                <small className="eyebrow">{product.category || "SIN CATEGORÍA"}</small>
                <h1>{product.name}</h1>
                {product.description && <p>{product.description}</p>}
                <div className="inv-detail-stats" data-testid="inventory-detail-stats">
                  <article><small>Existencias</small><b>{product.total_quantity} {product.unit || "un."}</b></article>
                  <article><small>Costo unit.</small><b>{money(product.cost)}</b></article>
                  <article><small>Precio venta</small><b>{money(product.selling_price)}</b></article>
                  <article className="highlight"><small>Margen</small><b>{product.margin}%</b></article>
                  <article><small>Valor en stock</small><b>{money(product.inventory_value)}</b></article>
                  <article><small>Ganancia potencial</small><b>{money(product.potential_profit)}</b></article>
                </div>
                {product.is_low_stock && (
                  <p className="inv-warn"><AlertTriangle size={14} /> Stock bajo · Umbral: {product.low_stock_threshold || 0} {product.unit || "un."}</p>
                )}
                <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                  <button type="button" className="cash-btn ghost" onClick={() => setEditing(true)} data-testid="inventory-detail-edit-button"><Pencil size={14} /> Editar</button>
                  <button type="button" className="cash-btn danger" onClick={remove} data-testid="inventory-detail-delete-button"><Trash2 size={14} /> Eliminar</button>
                </div>
              </>
            )}
          </div>
        </section>

        {error && <p className="profile-error">{error}</p>}

        <section className="inv-detail-columns">
          <article className="inv-panel" data-testid="inventory-detail-variations">
            <header>
              <div><Layers size={16} /> <b>Variaciones</b></div>
              <button type="button" onClick={() => setShowVariation((v) => !v)} data-testid="inventory-add-variation-button"><Plus size={14} /> Añadir</button>
            </header>
            {showVariation && (
              <form onSubmit={addVariation} className="inv-variation-form">
                <input placeholder="Nombre" value={variationForm.name} onChange={(e) => setVariationForm({ ...variationForm, name: e.target.value })} data-testid="inventory-variation-name" />
                <input placeholder="Talla" value={variationForm.size} onChange={(e) => setVariationForm({ ...variationForm, size: e.target.value })} />
                <input placeholder="Color" value={variationForm.color} onChange={(e) => setVariationForm({ ...variationForm, color: e.target.value })} />
                <input placeholder="Modelo" value={variationForm.model} onChange={(e) => setVariationForm({ ...variationForm, model: e.target.value })} />
                <input type="number" step="1" min="0" placeholder="Cantidad" value={variationForm.quantity} onChange={(e) => setVariationForm({ ...variationForm, quantity: e.target.value })} data-testid="inventory-variation-quantity" />
                <input placeholder="Ubicación" value={variationForm.location} onChange={(e) => setVariationForm({ ...variationForm, location: e.target.value })} data-testid="inventory-variation-location" />
                <button type="submit" className="cash-btn primary" data-testid="inventory-variation-save"><Save size={13} /> Guardar</button>
              </form>
            )}
            {(product.variations || []).length === 0 ? (
              <p className="inv-empty-mini">Aún no hay variaciones. Añade una si tu producto tiene tallas, colores o modelos.</p>
            ) : (
              <ul className="inv-variation-list">
                {(product.variations || []).map((v) => (
                  <li key={v.sku} data-testid={`inventory-variation-${v.sku}`}>
                    <div>
                      <b>{v.name || "Variación"}</b>
                      <small>{[v.size, v.color, v.model].filter(Boolean).join(" · ") || "Sin atributos"} · {v.location || "Sin ubicación"}</small>
                    </div>
                    <div className="inv-variation-qty">
                      <button type="button" onClick={() => updateVariationQuantity(v.sku, -1)} data-testid={`inventory-variation-minus-${v.sku}`}>−</button>
                      <b>{v.quantity || 0}</b>
                      <button type="button" onClick={() => updateVariationQuantity(v.sku, 1)} data-testid={`inventory-variation-plus-${v.sku}`}>+</button>
                    </div>
                    <button type="button" className="inv-icon-btn" onClick={() => deleteVariation(v.sku)} data-testid={`inventory-variation-delete-${v.sku}`}><Trash2 size={13} /></button>
                  </li>
                ))}
              </ul>
            )}
          </article>

          <article className="inv-panel" data-testid="inventory-detail-locations">
            <header><MapPin size={16} /> <b>Ubicaciones</b></header>
            {locations.length === 0 ? (
              <p className="inv-empty-mini">Añade una ubicación al producto o a sus variaciones para saber dónde encontrarlo.</p>
            ) : (
              <ul className="inv-location-list">
                {locations.map((l) => (
                  <li key={l.location}><span>{l.location}</span><b>{l.quantity} un.</b></li>
                ))}
              </ul>
            )}
          </article>
        </section>

        <section className="inv-panel" data-testid="inventory-detail-movements">
          <header><b>Movimientos</b></header>
          <form onSubmit={recordMovement} className="inv-movement-form">
            <select value={movementForm.movement_type} onChange={(e) => setMovementForm({ ...movementForm, movement_type: e.target.value })} data-testid="inventory-movement-type">
              <option value="entry">Entrada</option>
              <option value="exit">Salida</option>
              <option value="adjustment">Ajuste a total</option>
            </select>
            <input type="number" step="0.01" min="0" placeholder="Cantidad" value={movementForm.quantity} onChange={(e) => setMovementForm({ ...movementForm, quantity: e.target.value })} required data-testid="inventory-movement-quantity" />
            {(product.variations || []).length > 0 && (
              <select value={movementForm.variation_sku} onChange={(e) => setMovementForm({ ...movementForm, variation_sku: e.target.value })} data-testid="inventory-movement-variation">
                <option value="">Producto general</option>
                {product.variations.map((v) => <option key={v.sku} value={v.sku}>{v.name || v.sku}</option>)}
              </select>
            )}
            <input placeholder="Ubicación (opcional)" value={movementForm.location} onChange={(e) => setMovementForm({ ...movementForm, location: e.target.value })} data-testid="inventory-movement-location" />
            <input placeholder="Nota" value={movementForm.note} onChange={(e) => setMovementForm({ ...movementForm, note: e.target.value })} data-testid="inventory-movement-note" />
            <button type="submit" className="cash-btn primary" disabled={movementSaving} data-testid="inventory-movement-save">
              {movementSaving ? "..." : "Registrar"}
            </button>
          </form>
          {movements.length === 0 ? (
            <p className="inv-empty-mini">Aún no hay movimientos registrados.</p>
          ) : (
            <ul className="inv-movement-list">
              {movements.map((m) => (
                <li key={m.id} className={`mov-${m.movement_type}`} data-testid={`inventory-movement-${m.id}`}>
                  {m.movement_type === "entry" ? <ArrowDown size={16} /> : m.movement_type === "exit" ? <ArrowUp size={16} /> : <Layers size={16} />}
                  <div>
                    <b>{MOVEMENT_LABEL[m.movement_type]} · {m.quantity}</b>
                    <small>{new Date(m.created_at).toLocaleString("es-MX")} · {m.location || "sin ubicación"} {m.variation_sku ? `· ${m.variation_sku}` : ""}</small>
                    {m.note && <em>{m.note}</em>}
                  </div>
                  {m.cost && <span className="mov-cost">{money(m.cost)}</span>}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </AppShell>
  );
}
